import { useState } from 'react';
import { Alert, AuthLayout, Field, PasswordField, SubmitButton } from '@msarat/theme/components';

import { useI18n } from '../lib/i18n.jsx';

import { api } from '../lib/api.js';

/**
 * Sign-in for both audiences, on one screen with an explicit switch.
 *
 * A single screen with a visible parent/student toggle beats two separate URLs:
 * a child who lands on the parent form otherwise has no idea why their username
 * is rejected. The toggle makes the two identities obvious.
 *
 * Parents sign in with an email; students with a username and no email at all.
 */
export function Login({ onSignedIn, onGoToRegister }) {
  const { t } = useI18n();
  const [mode, setMode] = useState('parent');
  const [values, setValues] = useState({ email: '', username: '', password: '' });
  const [formError, setFormError] = useState('');
  const [pending, setPending] = useState(false);

  const isParent = mode === 'parent';

  const set = (key) => (value) => setValues((current) => ({ ...current, [key]: value }));

  function switchMode(next) {
    setMode(next);
    setFormError('');
    // Clear the identifier but keep nothing else: a student should not find a
    // parent's email prefilled on a shared tablet.
    setValues({ email: '', username: '', password: '' });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError('');
    setPending(true);

    try {
      const result = isParent
        ? await api.loginParent({ email: values.email.trim(), password: values.password })
        : await api.loginStudent({ username: values.username.trim(), password: values.password });
      onSignedIn?.(result.type);
    } catch (error) {
      // The server does not distinguish "no such account" from "wrong
      // password", and neither does this message.
      setFormError(error.message);
      setValues((current) => ({ ...current, password: '' }));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout
      brandLabel={t('brand.name')}
      title={isParent ? t('login.parentTitle') : t('login.studentTitle')}
      subtitle={isParent ? t('login.parentSubtitle') : t('login.studentSubtitle')}
      footer={
        isParent ? (
          <>
            {t('login.noAccount')}{' '}
            <a href="/register" onClick={(event) => { event.preventDefault(); onGoToRegister?.(); }}>
              {t('login.createAccount')}
            </a>
          </>
        ) : (
          t('login.studentFooter')
        )
      }
    >
      {/* A radiogroup rather than tabs: this chooses which credentials are being
          entered, which is a choice, not a view. */}
      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <span className="field__label" id="mode-label">
          {t('login.who')}
        </span>
        <div role="radiogroup" aria-labelledby="mode-label" style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {[
            { value: 'parent', label: t('login.asParent') },
            { value: 'student', label: t('login.asStudent') },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={mode === option.value}
              className={`button ${mode === option.value ? 'button--primary' : 'button--secondary'}`}
              style={{ flex: 1 }}
              onClick={() => switchMode(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <form className="stack" onSubmit={handleSubmit} noValidate>
        <Alert kind="error">{formError}</Alert>

        {isParent ? (
          <Field
            id="email"
            label={t('login.email')}
            type="email"
            value={values.email}
            onChange={set('email')}
            required
            autoComplete="username"
            inputMode="email"
            ltr
          />
        ) : (
          <Field
            id="username"
            label={t('login.username')}
            value={values.username}
            onChange={set('username')}
            required
            autoComplete="username"
            ltr
            hint={t('login.usernameHint')}
          />
        )}

        <PasswordField
          id="password"
          label={t('login.password')}
          value={values.password}
          onChange={set('password')}
          required
          autoComplete="current-password"
          showLabel={t('common.showPassword')}
          hideLabel={t('common.hidePassword')}
        />

        <SubmitButton pending={pending} pendingLabel={t('login.pending')}>
          {t('login.submit')}
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}
